import React, { useEffect, useState, useRef } from 'react';
import { getTempOpInstruction, deleteTempOpInstruction, getTempOpInstructionDetail } from '../../../services/temporaryOpInstructionService';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../components/Loader/Loader';
import { Pencil, Trash2 } from 'lucide-react';
import TemporaryOperatingInstructionModal from './TemporaryOperatingInstructionModal.tsx';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import { useSelector } from 'react-redux';
import Pagination from '../../../components/Pagination/Pagination';

interface TempOpInstruction {
  id: number;
  reference_number: string;
  title: string;
  site: string | null;
  created_at: string;
  status: string | null;
}

interface TemporaryOperatingInstructionTableProps {
  onError?: (message: string) => void;
  searchQuery?: string;
}

const ITEMS_PER_PAGE = 10;

const TemporaryOperatingInstructionTable: React.FC<TemporaryOperatingInstructionTableProps> = ({ onError, searchQuery = '' }) => {
  const navigate = useNavigate();

  /**  USER ROLE */
  const role = useSelector((state: any) => state.user.role);
  const isShiftSupervisor = role === 'shift_supervisor';

  const [allData, setAllData] = useState<TempOpInstruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(allData.length / ITEMS_PER_PAGE));
  const data = allData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  /**  MODAL STATE */
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState<any | null>(null);

  /**  DELETE MODAL STATE */
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  /**  SUCCESS MODAL STATE */
  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });

  const prevSearchQuery = useRef(searchQuery);

  const isEditableStatus = (status: string | null) => (status ?? 'New') === 'New';

  const fetchData = async (search = '') => {
    try {
      setLoading(true);
      const response = await getTempOpInstruction(1, search);

      const items = Array.isArray(response) ? response : response?.data || [];

      setAllData(items);
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to fetch TOI:', error);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: number, status: string | null) => {
    if (!isEditableStatus(status)) return;
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const showSuccessMessage = (title: string, message: string) => {
    setSuccessMessage({ title, message });
    setSuccessModal(true);
  };

  useEffect(() => {
    const isSearchChange = prevSearchQuery.current !== searchQuery;
    prevSearchQuery.current = searchQuery;

    if (isSearchChange) {
      setCurrentPage(1);
    }

    fetchData(searchQuery);
  }, [searchQuery]);

  if (loading) return <Loader />;

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'New':
        return 'text-blue-500';

      case 'Pending Instruction Addition':
        return 'text-orange-500';

      case 'Instructions Added':
        return 'text-cyan-500';

      case 'Review Pending':
        return 'text-yellow-500';

      case 'Reviewed':
        return 'text-green-500';

      case 'Pending Support':
        return 'text-amber-500';

      case 'Supported':
        return 'text-emerald-500';

      case 'Pending Authorization':
        return 'text-purple-500';

      case 'Authorized':
        return 'text-indigo-500';

      case 'Acceptence Pending':
        return 'text-pink-500';

      case 'Accepted':
        return 'text-green-700';

      case 'Extension Pending Approval':
        return 'text-orange-700';

      case 'Extension Approved':
        return 'text-teal-600';

      case 'Extension Rejected':
        return 'text-red-600';

      case 'Removed':
        return 'text-red-700';

      case 'Expired':
        return 'text-gray-600';

      case 'Cancelled':
        return 'text-slate-500';

      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm text-left border rounded shadow">
        <thead className="text-gray-500 text-xs">
          <tr className="h-[45px]">
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Sl. No</th>

            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Ref No.</th>

            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Created Date</th>

            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Title</th>

            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Site</th>

            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Status</th>

            {isShiftSupervisor && <th className="px-4 py-2 border text-center">Actions</th>}
          </tr>
        </thead>

        <tbody className="bg-white divide-y">
          {data.map((row, idx) => (
            <tr
              key={row.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => navigate(`/TemporaryOperatingInstruction/${row.id}`)}
            >
              <td className="border px-4 py-2 text-gray-500 text-center">{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
              <td className="border px-4 py-2 font-medium">{row.reference_number}</td>
              <td className="border px-4 py-2 font-medium">{new Date(row.created_at).toLocaleDateString('en-GB')}</td>
              <td className="border px-4 py-2 font-medium max-w-[250px]" title={row.title}>
                <div className="truncate">{row.title}</div>
              </td>
              <td className="border px-4 py-2 font-medium">{row.site ?? '-'}</td>
              <td className="border px-4 py-2 font-medium">
                <span className={getStatusColor(row.status ?? 'New')}>● {row.status ?? 'New'}</span>
              </td>

              {isShiftSupervisor && (
                <td className="border px-4 py-2" onClick={(e) => e.stopPropagation()}>
                  {isEditableStatus(row.status) ? (
                    <div className="flex justify-center gap-3">
                      {/* EDIT  */}
                      <button
                        className="text-blue-500"
                        onClick={async () => {
                          try {
                            const fullData = await getTempOpInstructionDetail(row.id);
                            setEditData(fullData);
                            setOpenModal(true);
                          } catch (err: any) {
                            console.error('Error fetching TOI detail:', err);
                            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load data for editing';
                            onError?.(errorMessage);
                          }
                        }}
                      >
                        <Pencil size={16} />
                      </button>

                      {/* DELETE */}
                      <button className="text-red-500" onClick={() => handleDelete(row.id, row.status)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-center text-gray-300">—</div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/*  EDIT MODAL */}
      {openModal && (
        <TemporaryOperatingInstructionModal
          editData={editData}
          onClose={() => {
            setOpenModal(false);
            setEditData(null);
          }}
          onSuccess={() => {
            fetchData(searchQuery);
            setOpenModal(false);
            setEditData(null);
            showSuccessMessage('TOI Updated', 'The Temporary Operating Instruction has been successfully updated.');
          }}
          onError={(message) => {
            onError?.(message);
          }}
        />
      )}

      {/*  DELETE CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        title="Are you sure you want to delete this Temporary Operating Instruction?"
        message=""
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onCancel={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        onConfirm={async () => {
          if (deleteId !== null) {
            try {
              await deleteTempOpInstruction(deleteId);
              fetchData(searchQuery);
              showSuccessMessage('TOI Deleted', 'The Temporary Operating Instruction has been successfully deleted.');
            } catch (err: any) {
              console.error('Error deleting TOI:', err);
              const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete Temporary Operating Instruction';
              onError?.(errorMessage);
            }
          }
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
      />

      {/*  SUCCESS MODAL */}
      <SuccessModal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title={successMessage.title}
        message={successMessage.message}
        buttonText="OK"
        onButtonClick={() => setSuccessModal(false)}
      />
    </div>
  );
};

export default TemporaryOperatingInstructionTable;
